import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import {ActionType} from 'redux-promise-middleware';
import StorageUtils from '@spothero/utils/storage';
import Config from 'config';
import {DESTINATION_UPDATE} from 'destination/destination-actions';
import {ROUTER_DATA_LOAD_FULFILLED} from 'router/router-actions';
import {
    SEARCH_REQUEST_SET_TERM,
    SEARCH_REQUEST_SET_LOCATION,
    SEARCH_REQUEST_UPDATE_TIMES,
} from 'search-request/search-request-actions';
import {SPOT_UPDATE_SELECTED} from 'spot/spot-actions';
import {
    SPOTS_GET_RADIAL,
    SPOTS_RADIAL_INCREMENT,
    SPOTS_GET_TILED,
    SPOTS_TILED_INCREMENT,
    SPOTS_GET_AIRPORT,
    SPOTS_GET_MOBILE,
} from 'spots/spots-actions';
import {
    SEARCH_GET_AIRPORTS,
    SEARCH_SET_SORT_TYPE,
    SEARCH_TOGGLE_INFO_PANEL_VIEW,
    SEARCH_SET_MAP_INSTANCE,
    SEARCH_SET_NO_SPOTS_FOUND,
    SEARCH_SET_ZOOM,
    SEARCH_TOGGLE_MOBILE_LIST_VIEW_ACTIVE,
} from './search-actions';
import SearchUtils from './search-utils';

export const initialState = {
    isPending: false,
    data: {...SearchUtils.defaultSearchSettings},
    error: null,
};

export default function search(state = initialState, {type, payload, meta}) {
    switch (type) {
        case `${SEARCH_GET_AIRPORTS}_${ActionType.Fulfilled}`: {
            return {
                ...state,
                data: {
                    ...state.data,
                    airports: payload,
                },
            };
        }

        case SPOT_UPDATE_SELECTED: {
            const {
                data: {breadcrumbs},
            } = state;
            const isInfoPanelVisible = payload.spot
                ? false
                : (!breadcrumbs.city || !breadcrumbs.city.cityPageZoom) &&
                  (!breadcrumbs.event || !breadcrumbs.event.description) &&
                  (!breadcrumbs.destination ||
                      !breadcrumbs.destination.description)
                ? false
                : !StorageUtils.get(Config.infoPanelCookie, 'cookie');

            return {
                ...state,
                data: {
                    ...state.data,
                    isInfoPanelVisible,
                },
            };
        }

        case SEARCH_SET_SORT_TYPE: {
            return merge({}, state, {
                data: {
                    sortBy: payload.sortBy,
                },
            });
        }

        case SEARCH_REQUEST_SET_LOCATION: {
            const {title, geolocated} = payload;

            return merge({}, state, {
                data: {
                    destinationTitle: title,
                    geolocated,
                    breadcrumbs: {
                        activeBreadcrumb: {
                            label: title,
                        },
                    },
                },
            });
        }

        case SEARCH_REQUEST_UPDATE_TIMES: {
            return merge({}, state, {
                data: {
                    isDefaultTimes: false,
                },
            });
        }

        case ROUTER_DATA_LOAD_FULFILLED: {
            const {search: cleanedSearchState} = payload;

            if (cleanedSearchState) {
                return merge({}, state, cleanedSearchState);
            } else {
                return state;
            }
        }

        case `${SPOTS_GET_RADIAL}_${ActionType.Pending}`: {
            return merge({}, state, {
                isPending: true,
                data: {
                    showNoSpotsFound: false,
                    requestIncrements: meta.numRequests,
                    currentIncrement: 1,
                },
                error: null,
            });
        }

        case `${SPOTS_GET_RADIAL}_${ActionType.Fulfilled}`: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: 0,
                },
            });
        }

        case `${SPOTS_GET_RADIAL}_${ActionType.Rejected}`: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: 0,
                },
                error: payload,
            });
        }

        case SPOTS_RADIAL_INCREMENT: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: state.data.currentIncrement + 1,
                },
                error: null,
            });
        }

        case `${SPOTS_GET_TILED}_${ActionType.Pending}`: {
            return merge({}, state, {
                isPending: true,
                data: {
                    requestIncrements: meta.numRequests,
                    currentIncrement: 1,
                },
                error: null,
            });
        }

        case `${SPOTS_GET_TILED}_${ActionType.Fulfilled}`: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: 0,
                },
            });
        }

        case `${SPOTS_GET_TILED}_${ActionType.Rejected}`: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: 0,
                },
                error: payload,
            });
        }

        case SPOTS_TILED_INCREMENT: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: state.data.currentIncrement + 1,
                },
                error: null,
            });
        }

        case `${SPOTS_GET_AIRPORT}_${ActionType.Pending}`: {
            return merge({}, state, {
                data: {
                    showNoSpotsFound: false,
                    sortBy: 'featured',
                },
            });
        }

        case `${SPOTS_GET_MOBILE}_${ActionType.Pending}`: {
            return merge({}, state, {
                data: {
                    showNoSpotsFound: false,
                },
            });
        }

        case SEARCH_TOGGLE_INFO_PANEL_VIEW: {
            const isInfoPanelVisible = payload.isInfoPanelVisible === true;

            if (!isInfoPanelVisible) {
                StorageUtils.set(Config.infoPanelCookie, true, 'cookie');
            } else {
                StorageUtils.set(Config.infoPanelCookie, false, 'cookie');
            }

            return merge({}, state, {
                data: {
                    isInfoPanelVisible,
                },
            });
        }

        case SEARCH_TOGGLE_MOBILE_LIST_VIEW_ACTIVE: {
            return merge({}, state, {
                data: {
                    isMobileListViewActive: payload.isMobileListViewActive,
                },
            });
        }

        case SEARCH_SET_MAP_INSTANCE: {
            return merge({}, state, {
                data: {
                    searchMapInstance: payload.searchMapInstance,
                },
            });
        }

        case SEARCH_SET_NO_SPOTS_FOUND: {
            return merge({}, state, {
                data: {
                    showNoSpotsFound: payload.showNoSpotsFound,
                },
            });
        }

        case SEARCH_SET_ZOOM: {
            return merge({}, state, {
                data: {
                    zoom: payload.zoom,
                },
            });
        }

        case DESTINATION_UPDATE: {
            return merge({}, state, {
                data: {
                    searchType: payload.searchType,
                },
            });
        }

        case SEARCH_REQUEST_SET_TERM: {
            const {monthly, city, destination = {}} = payload;
            const {zoom} = state;
            const hasDestination = !isEmpty(destination);

            const updatedZoom =
                city || hasDestination
                    ? city
                        ? SearchUtils.getCityZoom({
                              city,
                              monthly,
                              mobile: Config.isMobile,
                          })
                        : hasDestination
                        ? destination.override_ideal_zoom &&
                          (destination.desktop_zoom_level ||
                              destination.mobile_zoom_level)
                            ? SearchUtils.getDestinationZoom({
                                  destination,
                                  monthly,
                                  mobile: Config.isMobile,
                              })
                            : destination.default_map_zoom_level ||
                              city.default_map_zoom_level
                        : zoom
                    : zoom;

            return merge({}, state, {
                data: {
                    zoom: updatedZoom,
                },
            });
        }

        default:
            return state;
    }
}
